/**
 * src/components/spatial/SpatialObjectInteractionPanel.tsx
 *
 * Floating panel that appears after a public user clicks a spatial
 * object. Implements the integration-guide flow EXACTLY:
 *
 *   1. Call /interactions to get declared verbs + linked rule.
 *   2. User picks an action.
 *   3. POST /interact { interaction_type, actor_wallet, actor_lng,
 *      actor_lat, payload? } → { interaction: { id, decision,
 *      decisionReason } }.
 *      • If decision === 'denied' → show reason, stop.
 *   4. If the action invokes a contract:
 *      a. POST /execute { actor_wallet, interaction_id,
 *         function_name?, function_parameters? }
 *         → { status: 'authorized', execution: { id, contractAddress,
 *             functionName, functionParameters, … } }.
 *      b. Sign + broadcast the Soroban tx using execution.* fields
 *         via the host bridge.
 *      c. PATCH /executions/:execId { status: 'executed', tx_hash }
 *         (or 'failed') to close the audit trail.
 *
 *  GeoTrust NEVER broadcasts a Soroban tx without a 200 response from
 *  /execute. Bridges that prompt for wallet / passkey / payment / KYC
 *  / location run only AFTER the /interact step approves the verb
 *  (or are presented up front to satisfy preconditions for
 *  contract-execution actions).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { geoLinkSpatialObjectsClient, isContractVerb } from '../../services/spatial/geoLinkSpatialObjectsClient';
import { useMapSurface } from '../map/mapOverlayStore';
import { surfaceZIndex } from '../map/mapSurfaces';
import type { Rect } from '../map/mapTooltipPlacement';
import type {
  SpatialObjectExecuteResponse,
  SpatialObjectFeature,
  SpatialObjectInteractRequest,
  SpatialObjectInteractResponse,
  SpatialObjectInteractionAction,
  SpatialObjectInteractions,
  SpatialObjectType,
} from '../../services/spatial/geoLinkTypes';
import { flagImgProps } from '../../utils/countryVaultUtils';
import { reverseGeocodeLatLng } from '../../services/teleportReverseGeocode';
import { describeParams, type ParamDisclosure } from '../../services/spatial/spatialContractExecution';

/**
 * Bridge functions the parent provides so the panel can ask the host
 * app to satisfy preconditions for contract-executing actions.
 */
export interface InteractionBridges {
  /** Connect a wallet and return its public key. */
  ensureWallet?: () => Promise<{ publicKey: string } | null>;
  /** Register / verify a passkey. */
  ensurePasskey?: () => Promise<{ publicKeySPKI: string } | null>;
  /** Charge the user the indicated asset/amount. Resolves with a tx hash on success. */
  ensurePayment?: (asset: string, amount: string) => Promise<{ txHash: string } | null>;
  /** Resolve the user's current location (lat/lng) accurate enough for the gate. */
  ensureLocation?: (withinMeters: number) => Promise<{ latitude: number; longitude: number; accuracy?: number } | null>;
  /** Verify the user has completed KYC at the requested level. */
  ensureKyc?: (level: string | undefined) => Promise<boolean>;
  /**
   * Sign + broadcast the Soroban transaction described by the
   * authorisation envelope returned by /execute. Resolves with the
   * Stellar tx hash on success, or `null` to abort.
   */
  signAndSubmit?: (envelope: {
    contractAddress: string;
    functionName: string;
    functionParameters: Record<string, unknown>;
    actorWallet: string;
  }) => Promise<{ txHash: string } | null>;
  /**
   * Sign an already-built envelope, without submitting it.
   *
   * The treasury flow needs this rather than `signAndSubmit`: the server
   * built the transaction and the server submits it, so all the host has
   * to contribute is a signature. `latestLedger` comes from the
   * simulation and is what a smart account signs its expiry over.
   */
  signXdr?: (xdr: string, latestLedger?: number) => Promise<string>;
}

export interface SpatialObjectInteractionPanelProps {
  /** Spatial object the user clicked on. */
  feature: SpatialObjectFeature | null;
  /** Click position in screen pixels — used to anchor the panel. */
  anchorPx?: { x: number; y: number };
  /** Caller's wallet public key (when known). */
  publicKey?: string | null;
  /** Last known device location. */
  location?: { latitude: number; longitude: number; accuracy?: number } | null;
  /** Bridges to the host app's wallet / passkey / payment / location stacks. */
  bridges?: InteractionBridges;
  /**
   * Optional preconditions per action verb. Pre-screens contract
   * actions: `{ wallet: true, payment: { asset, amount }, ... }` etc.
   * When null, the panel runs the action directly (using whatever
   * publicKey is already passed).
   */
  preconditionsFor?: (action: SpatialObjectInteractionAction) => {
    wallet?: boolean;
    passkey?: boolean;
    payment?: { asset: string; amount: string };
    location?: { withinMeters: number };
    kyc?: { level?: string };
  } | null;
  /**
   * Open the event surface for this object, when the host has one.
   *
   * Two entry points, one handler: an object that already has an
   * occasion opens it, and a venue that could host one opens the
   * composer. Both are requests — the panel does not own either
   * surface, and the server decides whether the second is allowed.
   */
  onOpenEvents?: (input: { objectId: string; create: boolean }) => void;
  /**
   * Open a conversation with this object.
   *
   * The door that was missing. A conversation could only be started
   * from the hover card, and a touch device has no hover — so on a
   * phone the card that says an object talks was unreachable and this
   * panel, the one thing a tap does open, offered no way to talk to
   * it. No wallet is asked for; the server rations questions instead.
   */
  onChat?: (input: { objectId: string }) => void;
  /** Close handler. */
  onClose: () => void;
  /** Called once an action successfully executes on-chain. */
  onActionCompleted?: (action: SpatialObjectInteractionAction, txHash: string | undefined) => void;
}

const cardStyle: React.CSSProperties = {
  position:      'absolute',
  // The coordinator's rank for an inspector, not a number of its own.
  // At 2400 this panel lost to every card in the ladder — including
  // the mobile country tooltip, which covered it completely on the one
  // device where this panel is the only way in.
  zIndex:        surfaceZIndex('inspector'),
  background:    'rgba(15, 23, 42, 0.96)',
  color:         '#e2e8f0',
  borderRadius:  12,
  padding:       '14px 16px',
  boxShadow:     '0 8px 32px rgba(0, 0, 0, 0.55)',
  border:        '1px solid rgba(99, 102, 241, 0.45)',
  font:          '500 12.5px/1.45 system-ui, -apple-system, sans-serif',
  pointerEvents: 'auto',
  minWidth:      300,
  maxWidth:      'min(calc(100vw - 32px), 380px)',
};

const buttonBase: React.CSSProperties = {
  border:       '1px solid rgba(148, 163, 184, 0.3)',
  background:   'rgba(30, 41, 59, 0.85)',
  color:        '#e2e8f0',
  borderRadius: 8,
  padding:      '7px 12px',
  font:         '600 12px system-ui, -apple-system, sans-serif',
  cursor:       'pointer',
};
const primaryBtn: React.CSSProperties = {
  ...buttonBase,
  background:  'linear-gradient(135deg, #6366f1, #8b5cf6)',
  borderColor: 'transparent',
  color:       '#fff',
};

type FlowStatus =
  | 'idle'
  | 'loading-interactions'
  | 'satisfying-preconditions'
  | 'recording-interaction'
  | 'authorizing-execution'
  | 'awaiting-confirmation'
  | 'signing'
  | 'closing-audit'
  | 'done'
  | 'error';

/**
 * An authorisation waiting for the user to approve it.
 *
 * GeoLink has said yes by this point; the wallet has not been asked
 * anything yet. Held in state rather than awaited inside `runAction` so
 * that "nothing is in flight" is a fact about the component and not a
 * promise someone could still resolve — closing the panel here cannot
 * sign, because there is no continuation to resume.
 */
interface PendingExecution {
  action: SpatialObjectInteractionAction;
  execution: SpatialObjectExecuteResponse['execution'];
  /** Computed once, when the authorisation arrives, not during render. */
  params: ParamDisclosure[];
  actorWallet: string;
}

/**
 * Render an authorized parameter value without interpreting it.
 *
 * Strings pass through as themselves, so an address or a decimal reads
 * the way GeoLink sent it. Everything else becomes JSON, which keeps
 * numbers unscaled and shapes visible. No units, no thousands
 * separators, no rounding — this is the value the contract receives,
 * and the only honest thing to show is the value the contract receives.
 */
function formatDisclosedValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'bigint') return value.toString();
  if (value === undefined) return 'undefined';
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

/** Mapbox often JSON-stringifies nested property objects on click. */
function asMetaBag(raw: unknown): Record<string, unknown> {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw === 'string' && raw.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch { /* ignore */ }
  }
  return {};
}

/** Best-effort ISO2 from Map API / metadata fields. */
function iso2FromProps(props: Record<string, unknown>, meta: Record<string, unknown>): string | null {
  for (const v of [
    props.countryCode, props.country_code,
    meta.countryCode, meta.country_code, meta.country,
  ]) {
    if (typeof v !== 'string') continue;
    const s = v.trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(s)) return s;
  }
  return null;
}

/** Rough centroid for reverse-geocoding a country when the API omitted it. */
function featureCentroid(feature: SpatialObjectFeature): { lng: number; lat: number } | null {
  const g = feature.geometry as { type?: string; coordinates?: unknown } | null;
  if (!g?.coordinates) return null;
  const walk = (coords: unknown): number[][] => {
    if (!Array.isArray(coords) || coords.length === 0) return [];
    if (typeof coords[0] === 'number') return [coords as number[]];
    return (coords as unknown[]).flatMap(walk);
  };
  const pts = walk(g.coordinates).filter((p) => p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  if (pts.length === 0) return null;
  let lng = 0;
  let lat = 0;
  for (const p of pts) { lng += p[0]; lat += p[1]; }
  return { lng: lng / pts.length, lat: lat / pts.length };
}

function shortWallet(pk: string): string {
  const s = pk.trim();
  if (s.length <= 12) return s;
  return `${s.slice(0, 4)}…${s.slice(-4)}`;
}

/**
 * Peak extrusion for stepped buildings. Clicking a setback feature often
 * carries that tier's height (e.g. 35m) in `heightMeters` — the true
 * building height is max(metadata.tiers[].height, peakHeightMeters, …).
 */
function peakHeightMeters(
  props: Record<string, unknown>,
  meta: Record<string, unknown>,
): { peak: number | null; base: number | null; tierCount: number } {
  const num = (v: unknown): number | null => {
    const n = typeof v === 'string' ? Number(v) : typeof v === 'number' ? v : NaN;
    return Number.isFinite(n) ? n : null;
  };
  // Prefer an explicit peak stamped at click/normalize time.
  let peak =
    num(props.peakHeightMeters) ??
    num(meta.peakHeightMeters) ??
    num(props.heightMeters) ??
    num(props.height) ??
    num(meta.heightMeters) ??
    num(meta.height);
  let base = num(props.baseHeightMeters) ?? num(props.base_height) ?? num(meta.baseHeightMeters) ?? num(meta.min_height);
  let tierCount = 0;
  let rawTiers: unknown = meta.tiers ?? props.tiers;
  if (typeof rawTiers === 'string' && rawTiers.trim().startsWith('[')) {
    try { rawTiers = JSON.parse(rawTiers); } catch { rawTiers = null; }
  }
  if (Array.isArray(rawTiers)) {
    for (const t of rawTiers) {
      if (!t || typeof t !== 'object') continue;
      const r = t as Record<string, unknown>;
      const h = num(r.height);
      const mn = num(r.min_height);
      if (h != null) {
        tierCount += 1;
        peak = peak == null ? h : Math.max(peak, h);
      }
      if (mn != null) {
        base = base == null ? mn : Math.min(base, mn);
      }
    }
  }
  const totalTiers = num(props.totalTiers);
  if (totalTiers != null && totalTiers > tierCount) tierCount = Math.floor(totalTiers);
  return { peak, base, tierCount };
}

const SpatialObjectInteractionPanel: React.FC<SpatialObjectInteractionPanelProps> = ({
  feature,
  anchorPx,
  publicKey,
  location,
  bridges,
  preconditionsFor,
  onOpenEvents,
  onChat,
  onClose,
  onActionCompleted,
}) => {
  const [interactions, setInteractions] = useState<SpatialObjectInteractions | null>(null);
  const [status, setStatus] = useState<FlowStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [lastTxHash, setLastTxHash] = useState<string | null>(null);
  const [lastDecision, setLastDecision] = useState<SpatialObjectInteractResponse['interaction'] | null>(null);
  const [resolvedCountry, setResolvedCountry] = useState<string | null>(null);
  const [connectingWallet, setConnectingWallet] = useState(false);
  const [pending, setPending] = useState<PendingExecution | null>(null);

  /**
   * The single-shot latch on signing.
   *
   * State is not enough on its own: two clicks in the same tick both see
   * the old `pending` and both would proceed. A ref flips synchronously,
   * so the second click is already too late by the time it reads it.
   */
  const signingRef = useRef(false);

  /**
   * Announce this panel to the overlay coordinator.
   *
   * It is an `inspector`: a surface the visitor opened deliberately, so
   * hover cards get out of its way rather than the reverse. The
   * rectangle is measured from the rendered node instead of predicted,
   * because this panel sizes itself to its content — a building with
   * four actions and one with none are different shapes, and a guessed
   * rectangle would push other cards away from empty space.
   */
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [panelRect, setPanelRect] = useState<Rect | null>(null);
  const surfaceObjectId = feature?.properties.objectId ?? '';
  const anchorKey = anchorPx ? `${anchorPx.x},${anchorPx.y}` : '';

  useEffect(() => {
    const node = panelRef.current;
    if (!node) {
      setPanelRect(null);
      return;
    }
    const box = node.getBoundingClientRect();
    const next: Rect = {
      left: box.left, top: box.top, right: box.right, bottom: box.bottom,
    };
    setPanelRect((prev) => (
      prev && prev.left === next.left && prev.top === next.top
        && prev.right === next.right && prev.bottom === next.bottom
        ? prev
        : next
    ));
    // `status` is in the dependency list because the panel grows as the
    // flow progresses — a decision badge and a tx hash both change its
    // height, and a stale rectangle is worse than none.
  }, [surfaceObjectId, anchorKey, status, interactions]);

  useMapSurface({
    id: `object-inspector-${surfaceObjectId}`,
    kind: 'inspector',
    rect: panelRect,
    enabled: !!feature && !!surfaceObjectId,
  });

  // Reset state when the spatial object changes.
  useEffect(() => {
    setInteractions(null);
    setStatus('idle');
    setError(null);
    setProgress(null);
    setLastTxHash(null);
    setLastDecision(null);
    setResolvedCountry(null);
  }, [feature?.properties.objectId]);

  // Fetch the interactions list when a feature is selected.
  useEffect(() => {
    if (!feature) return;
    // Guard against a missing id (e.g. a summary feature that hasn't been
    // normalized) so we never request `/spatial-objects/undefined/interactions`.
    const objectId = feature.properties.objectId;
    if (objectId == null || objectId === '' || String(objectId) === 'undefined') {
      setStatus('idle');
      setError(null);
      return;
    }
    let cancelled = false;
    setStatus('loading-interactions');
    setError(null);
    geoLinkSpatialObjectsClient.getSpatialObjectInteractions(objectId)
      .then(res => {
        if (cancelled) return;
        setInteractions(res);
        setStatus('idle');
      })
      .catch(err => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load interactions');
        setStatus('error');
      });
    return () => { cancelled = true; };
  }, [feature]);

  /**
   * Run an action through the integration-guide flow.
   *
   *   1. Satisfy host-app preconditions (wallet / payment / etc.) so
   *      the GeoLink call has the actor_wallet it needs.
   *   2. POST /interact — record the verb, check `decision`.
   *   3. If the action invokes a contract:
   *        POST /execute — get { status: 'authorized', execution: {...} }
   *        Sign + broadcast via the host bridge.
   *        PATCH /executions/:execId — close the audit trail.
   */
  const runAction = useCallback(async (action: SpatialObjectInteractionAction) => {
    if (!feature) return;
    const objectId = feature.properties.objectId;
    setError(null);
    setLastTxHash(null);
    setLastDecision(null);
    // A new action gets its own authorisation, so it gets its own latch.
    setPending(null);
    signingRef.current = false;

    let actorWallet = publicKey ?? null;
    let actorLocation = location ?? null;
    const interactPayload: SpatialObjectInteractRequest = {
      interaction_type: action.name,
      actor_wallet:     actorWallet,
      actor_lng:        actorLocation?.longitude ?? null,
      actor_lat:        actorLocation?.latitude  ?? null,
      payload:          {},
    };

    // ── 1. Preconditions ──────────────────────────────────────────
    const pre = preconditionsFor?.(action) ?? null;
    if (pre) {
      setStatus('satisfying-preconditions');
      try {
        if (pre.wallet) {
          setProgress('Connect a wallet to continue…');
          const w = await bridges?.ensureWallet?.();
          if (!w) throw new Error('Wallet connection cancelled');
          actorWallet = w.publicKey;
          interactPayload.actor_wallet = w.publicKey;
        }
        if (pre.passkey) {
          setProgress('Verify your passkey to continue…');
          const pk = await bridges?.ensurePasskey?.();
          if (!pk) throw new Error('Passkey verification cancelled');
          interactPayload.payload = { ...(interactPayload.payload ?? {}), passkeyPublicKeySPKI: pk.publicKeySPKI };
        }
        if (pre.location) {
          setProgress(`Confirming you're within ${pre.location.withinMeters} m…`);
          const loc = await bridges?.ensureLocation?.(pre.location.withinMeters);
          if (!loc) throw new Error('Location requirement could not be satisfied');
          actorLocation = loc;
          interactPayload.actor_lng = loc.longitude;
          interactPayload.actor_lat = loc.latitude;
        }
        if (pre.payment) {
          setProgress(`Charge ${pre.payment.amount} ${pre.payment.asset}?`);
          const pay = await bridges?.ensurePayment?.(pre.payment.asset, pre.payment.amount);
          if (!pay) throw new Error('Payment cancelled');
          interactPayload.payload = {
            ...(interactPayload.payload ?? {}),
            paymentTxHash: pay.txHash,
            paymentAsset:  pre.payment.asset,
            paymentAmount: pre.payment.amount,
          };
        }
        if (pre.kyc) {
          setProgress(`Verifying KYC (${pre.kyc.level || 'basic'})…`);
          const ok = await bridges?.ensureKyc?.(pre.kyc.level);
          if (!ok) throw new Error('KYC requirement not satisfied');
          interactPayload.payload = { ...(interactPayload.payload ?? {}), kycLevel: pre.kyc.level || 'basic' };
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        setProgress(null);
        setStatus('error');
        return;
      }
    }

    // ── 2. Record the interaction (always) ────────────────────────
    setStatus('recording-interaction');
    setProgress('Recording interaction with GeoLink…');
    let interactRes: SpatialObjectInteractResponse;
    try {
      interactRes = await geoLinkSpatialObjectsClient.interactWithSpatialObject(objectId, interactPayload);
      setLastDecision(interactRes.interaction);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      setProgress(null);
      setStatus('error');
      return;
    }

    if (interactRes.interaction.decision !== 'allowed') {
      setError(interactRes.interaction.decisionReason || 'GeoLink denied this interaction.');
      setProgress(null);
      setStatus('error');
      return;
    }

    // ── 3. If the action invokes a contract, authorise + sign + PATCH ─
    //
    // The verb decides, and only the verb. This used to also accept any
    // action carrying a `contractFunction`, which meant a malformed
    // action — `{ name: 'view', contractFunction: 'withdraw' }` — could
    // reach /execute and a signing prompt from a button labelled "View
    // metadata". The classification in actionsForVerbs no longer produces
    // that action, but this is the boundary that must refuse it: an
    // action is data, and data does not get to authorise its own
    // execution.
    const isContractAction = isContractVerb(action.name);
    if (!isContractAction) {
      setStatus('done');
      setProgress(null);
      onActionCompleted?.(action, undefined);
      return;
    }

    if (!actorWallet) {
      // Contract actions need a wallet — surface a clear error rather
      // than silently sending /execute without one (GeoLink would
      // reject it).
      setError('A connected wallet is required to execute this action.');
      setProgress(null);
      setStatus('error');
      return;
    }

    let executeRes: SpatialObjectExecuteResponse;
    try {
      setStatus('authorizing-execution');
      setProgress('Asking GeoLink to authorise execution…');
      executeRes = await geoLinkSpatialObjectsClient.executeSpatialObjectRule(objectId, {
        actor_wallet:        actorWallet,
        // Resent rather than inherited from the interaction: GeoLink
        // authorizes /execute independently and does not read the
        // location off the row that interaction_id points at.
        actor_lng:           actorLocation?.longitude ?? null,
        actor_lat:           actorLocation?.latitude  ?? null,
        interaction_id:      interactRes.interaction.id,
        function_name:       action.contractFunction || feature.properties.contractFunction || undefined,
        function_parameters: {},
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(`Authorization failed: ${message}`);
      setProgress(null);
      setStatus('error');
      return;
    }

    if (executeRes.status !== 'authorized') {
      setError('GeoLink did not authorise this execution.');
      setProgress(null);
      setStatus('error');
      return;
    }

    // ── 4. Hand the authorisation to the user, and stop here ──────────
    //
    // GeoLink has said yes; the wallet has not been asked anything yet.
    // What GeoLink authorised is not necessarily what the button
    // implied — `functionName` and `functionParameters` are resolved
    // server-side from the linked rule, and the panel sends none of
    // them — so this is the first and only moment the user can see the
    // actual call. `runAction` returns; `confirmExecution` continues.
    setStatus('awaiting-confirmation');
    setProgress(null);
    setPending({
      action,
      execution: executeRes.execution,
      params:    describeParams(executeRes.execution.functionParameters),
      actorWallet,
    });
  }, [feature, publicKey, location, bridges, preconditionsFor, onActionCompleted]);

  /**
   * Sign + broadcast an authorisation the user has just approved.
   *
   * Single-shot, and permanently so for this authorisation: the latch
   * is never lowered here. A signing failure leaves no Confirm button
   * to press again, which is the point — the same authorisation must
   * not be submittable twice by a frustrated second click. Starting a
   * new action is what lowers it.
   */
  const confirmExecution = useCallback(async () => {
    if (signingRef.current) return;
    const job = pending;
    if (!job) return;
    signingRef.current = true;

    // Dropped before the first `await`, so there is no window in which
    // a re-render could offer Confirm for an execution already signing.
    setPending(null);
    setStatus('signing');
    setProgress('Sign + broadcast in your wallet…');

    let txHash: string | null = null;
    let signError: string | null = null;
    try {
      if (!bridges?.signAndSubmit) {
        // No bridge available — we can't broadcast. Close the audit
        // trail with `failed` so GeoLink doesn't think the user is
        // about to broadcast.
        signError = 'No wallet bridge available to sign the Soroban transaction.';
      } else {
        const result = await bridges.signAndSubmit({
          contractAddress:    job.execution.contractAddress,
          functionName:       job.execution.functionName,
          functionParameters: job.execution.functionParameters,
          actorWallet:        job.actorWallet,
        });
        if (!result) {
          signError = 'Transaction signing cancelled';
        } else {
          txHash = result.txHash;
        }
      }
    } catch (err) {
      signError = err instanceof Error ? err.message : String(err);
    }

    // Close the audit trail (best-effort — never fails the UX).
    setStatus('closing-audit');
    setProgress('Closing GeoLink audit trail…');
    try {
      await geoLinkSpatialObjectsClient.reportExecutionResult(job.execution.id, {
        status:   txHash ? 'executed' : 'failed',
        tx_hash:  txHash,
        error:    signError,
      });
    } catch {
      // already swallowed inside the client
    }

    if (signError || !txHash) {
      setError(signError || 'Transaction did not broadcast');
      setProgress(null);
      setStatus('error');
      return;
    }

    setLastTxHash(txHash);
    setStatus('done');
    setProgress(null);
    onActionCompleted?.(job.action, txHash);
  }, [pending, bridges, onActionCompleted]);

  /**
   * Decline an authorisation the user has seen and does not want.
   *
   * GeoLink is told, because it is holding an open execution row that
   * would otherwise look like a transaction still on its way. The API
   * has no 'cancelled' status — only 'executed' | 'failed' — so this
   * reuses `failed` with a reason, the same way a missing wallet bridge
   * already does. Closing the panel instead reports nothing and leaves
   * that row open; see the safety-gap doc.
   */
  const cancelExecution = useCallback(async () => {
    if (signingRef.current) return;
    const job = pending;
    if (!job) return;
    setPending(null);
    setStatus('closing-audit');
    setProgress('Telling GeoLink the execution was cancelled…');
    try {
      await geoLinkSpatialObjectsClient.reportExecutionResult(job.execution.id, {
        status:  'failed',
        tx_hash: null,
        error:   'Cancelled before signing',
      });
    } catch {
      // already swallowed inside the client
    }
    setProgress(null);
    setError('Cancelled before signing — nothing was sent to your wallet.');
    setStatus('idle');
  }, [pending]);

  // Resolve country ISO2 from props/metadata; fall back to reverse geocode.
  useEffect(() => {
    if (!feature) return;
    const props = feature.properties as Record<string, unknown>;
    const meta = {
      ...(interactions?.metadata ?? {}),
      ...((props.metadata ?? {}) as Record<string, unknown>),
    };
    const fromProps = iso2FromProps(props, meta);
    if (fromProps) {
      setResolvedCountry(fromProps);
      return;
    }
    const c = featureCentroid(feature);
    if (!c) return;
    let cancelled = false;
    void reverseGeocodeLatLng(c.lat, c.lng).then((place) => {
      if (cancelled || !place?.country) return;
      const iso = place.country.trim().toUpperCase();
      if (/^[A-Z]{2}$/.test(iso)) setResolvedCountry(iso);
    });
    return () => { cancelled = true; };
  }, [feature, interactions?.metadata]);

  if (!feature) return null;

  const props = feature.properties;
  const propsAny = props as Record<string, unknown>;
  // Merge the object's metadata bag with the interactions echo (some
  // GeoLink builds only return the richer metadata on /interactions).
  // Parse stringified metadata — Mapbox click payloads often stringify nests.
  const meta = {
    ...(interactions?.metadata ?? {}),
    ...asMetaBag(props.metadata),
  } as Record<string, unknown> & {
    name?: string;
    title?: string;
    label?: string;
    address?: string;
    description?: string;
    realEstate?: { listingPriceUsd?: number; listingStatus?: string; deedContract?: string };
    real_estate?: { listingPriceUsd?: number; listingStatus?: string; deedContract?: string };
    event?: { title?: string; startsAt?: string; endsAt?: string; ticketContract?: string; venue?: string };
  };

  const objectType = (interactions?.objectType ?? props.objectType) as SpatialObjectType;
  const humanType = String(objectType ?? 'object').replace(/_/g, ' ');

  // Resolve a friendly name, never falling back to the raw UUID for the
  // headline (we show that as a small subtitle instead).
  const resolvedName =
    (interactions?.name && interactions.name.trim()) ||
    (typeof propsAny.displayName === 'string' && propsAny.displayName.trim()) ||
    (typeof meta.name === 'string' && meta.name.trim()) ||
    (typeof meta.title === 'string' && meta.title.trim()) ||
    (typeof meta.label === 'string' && meta.label.trim()) ||
    (typeof propsAny.name === 'string' ? propsAny.name.trim() : '') ||
    '';
  const nameForHeader = resolvedName || `${humanType.charAt(0).toUpperCase()}${humanType.slice(1)}`;
  const description =
    (interactions?.description && interactions.description.trim()) ||
    (typeof meta.description === 'string' ? meta.description.trim() : '') ||
    '';

  // Type-appropriate detail rows. Each entry renders only when its
  // value resolves, so every object type shows what's relevant to it.
  const event = (meta.event ?? null) as { title?: string; startsAt?: string; endsAt?: string; venue?: string; ticketContract?: string } | null;
  const realEstateData = meta.realEstate ?? meta.real_estate;
  // Prefer peak across metadata.tiers — a click on setback #t0 often has
  // heightMeters=35 while the tower peaks at 828m (Burj Khalifa).
  const { peak: heightM, base: baseM, tierCount } = peakHeightMeters(propsAny, meta);
  const countryIso2 = resolvedCountry || iso2FromProps(propsAny, meta);
  const creatorWallet =
    (typeof meta.createdByWallet === 'string' && meta.createdByWallet.trim()) ||
    (typeof meta.owner_wallet === 'string' && meta.owner_wallet.trim()) ||
    (typeof propsAny.ownerWallet === 'string' && propsAny.ownerWallet.trim()) ||
    '';
  const walletConnected = !!(publicKey && publicKey.trim());
  const isOwner = walletConnected && !!creatorWallet &&
    creatorWallet.toLowerCase() === publicKey!.trim().toLowerCase();
  const visibility =
    (typeof propsAny.visibility === 'string' && propsAny.visibility) ||
    (typeof meta.visibility === 'string' && meta.visibility) ||
    null;
  const statusLabel =
    (typeof propsAny.status === 'string' && propsAny.status) ||
    (typeof meta.status === 'string' && meta.status) ||
    null;
  const hasContractLink = !!(
    props.contractAddress ||
    interactions?.executionRule?.contractAddress ||
    props.executionRuleId ||
    interactions?.executionRule?.ruleId
  );

  // Remaining primitive metadata keys we haven't surfaced explicitly —
  // shown under a compact "More details" disclosure so nothing is lost.
  const KNOWN_META_KEYS = new Set([
    'name', 'title', 'label', 'description', 'address', 'event', 'realEstate',
    'real_estate', 'color_key', 'colorKey', 'interaction_types', 'interactionTypes',
    'tiers', 'createdByWallet', 'countryCode', 'country_code', 'country',
    'owner_wallet', 'visibility', 'status',
  ]);
  const extraMeta = Object.entries(meta)
    .filter(([k, v]) =>
      !KNOWN_META_KEYS.has(k) &&
      (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'))
    .slice(0, 12);

  // Pin the panel in viewport coordinates with a small offset so it
  // doesn't sit directly under the cursor.
  const left = anchorPx ? Math.max(12, Math.min(anchorPx.x + 16, window.innerWidth - 400)) : 16;
  const top  = anchorPx ? Math.max(12, Math.min(anchorPx.y + 16, window.innerHeight - 280)) : 16;

  const headerIcon =
    objectType === 'building'                 ? '🏢' :
    objectType === 'floor'                    ? '🏬' :
    objectType === 'zone'                     ? '🟦' :
    objectType === 'slice'                    ? '✂️' :
    objectType === 'real_estate'              ? '🏷' :
    objectType === 'event_object'             ? '🎟' :
    objectType === 'contract_execution_object'? '⚙' :
                                                '📍';

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Spatial object interactions"
      style={{ ...cardStyle, top, left }}
      onClick={e => e.stopPropagation()}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <span aria-hidden style={{ fontSize: 18, lineHeight: 1.2 }}>{headerIcon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: '#a5b4fc', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameForHeader}</span>
            {countryIso2 && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                <img
                  {...flagImgProps(countryIso2, 20)}
                  alt={`${countryIso2} flag`}
                  title={countryIso2}
                  style={{
                    ...flagImgProps(countryIso2, 20).style,
                    width: 20,
                    boxShadow: '0 0 0 1px rgba(148,163,184,0.4)',
                  }}
                />
                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{countryIso2}</span>
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {humanType}
            {props.floorNumber != null ? ` · floor ${props.floorNumber}` : ''}
            {visibility ? ` · ${visibility}` : ''}
          </div>
          <div
            title={props.objectId}
            style={{ fontSize: 9.5, color: '#64748b', fontFamily: 'ui-monospace, monospace', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            #{props.objectId}
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" style={{ ...buttonBase, padding: '2px 8px' }}>×</button>
      </div>

      {/* Session context — what anonymous vs connected users can do */}
      <div style={{
        marginTop: 10, padding: '7px 10px', borderRadius: 8,
        background: walletConnected ? 'rgba(34, 197, 94, 0.08)' : 'rgba(148, 163, 184, 0.08)',
        border: walletConnected
          ? '1px solid rgba(34, 197, 94, 0.35)'
          : '1px solid rgba(148, 163, 184, 0.25)',
        fontSize: 11, color: '#cbd5e1',
        display: 'flex', flexDirection: 'column', gap: 4,
      }}>
        {walletConnected ? (
          <>
            <div>
              Wallet <span style={{ color: '#86efac', fontFamily: 'ui-monospace, monospace' }}>{shortWallet(publicKey!)}</span>
              {isOwner && <span style={{ color: '#86efac', marginLeft: 6 }}>· you own this</span>}
            </div>
            {location && Number.isFinite(location.latitude) && Number.isFinite(location.longitude) && (
              <div style={{ color: '#94a3b8' }}>
                Location ready
                {location.accuracy != null && Number.isFinite(location.accuracy)
                  ? ` (±${Math.round(location.accuracy)} m)`
                  : ''}
              </div>
            )}
            {hasContractLink && (
              <div style={{ color: '#94a3b8' }}>On-chain actions available when GeoLink approves the verb.</div>
            )}
          </>
        ) : (
          <>
            <div style={{ color: '#e2e8f0' }}>Browsing as guest — public details &amp; info actions only.</div>
            {hasContractLink && (
              <div style={{ color: '#fde68a' }}>Connect a wallet to run linked contract actions.</div>
            )}
            {bridges?.ensureWallet && (
              <button
                type="button"
                disabled={connectingWallet}
                onClick={() => {
                  setConnectingWallet(true);
                  void bridges.ensureWallet!()
                    .catch(() => null)
                    .finally(() => setConnectingWallet(false));
                }}
                style={{ ...buttonBase, alignSelf: 'flex-start', marginTop: 2, opacity: connectingWallet ? 0.6 : 1 }}
              >
                {connectingWallet ? 'Connecting…' : 'Connect wallet'}
              </button>
            )}
          </>
        )}
        {creatorWallet && !isOwner && (
          <div style={{ color: '#64748b', fontFamily: 'ui-monospace, monospace', fontSize: 10 }}>
            Owner {shortWallet(creatorWallet)}
          </div>
        )}
        {statusLabel && (
          <div style={{ color: '#94a3b8' }}>Status · {statusLabel}</div>
        )}
      </div>

      {/* The ways out of this panel and into a surface that answers
          questions. Above the details rather than below them, because
          on a phone the details are long and the conversation was the
          thing nobody could find — a door at the bottom of a scroll is
          not much better than no door.

          The event button reads "open" for an object that already has
          an occasion and "put one on" for a venue that could host one,
          because those are the two things a person standing in front
          of a building asks. The conversation sits beside it. */}
      {(onOpenEvents || onChat) && typeof props.objectId === 'string' && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
          {onChat && (
            <button
              type="button"
              onClick={() => onChat({ objectId: props.objectId as string })}
              title={`Ask ${nameForHeader} a question — no wallet needed`}
              style={{
                border: 'none', borderRadius: 8, padding: '5px 10px', fontSize: 11,
                fontWeight: 700, background: 'rgba(99,102,241,0.24)', color: '#a5b4fc',
                cursor: 'pointer',
              }}
            >
              Talk to it
            </button>
          )}
          {onOpenEvents && (
            <button
              type="button"
              onClick={() => onOpenEvents({
                objectId: props.objectId as string, create: !event?.title,
              })}
              style={{
                border: 'none', borderRadius: 8, padding: '5px 10px', fontSize: 11,
                fontWeight: 700, background: 'rgba(168,85,247,0.22)', color: '#d8b4fe',
                cursor: 'pointer',
              }}
            >
              {event?.title ? 'Open event' : 'Put on an event here'}
            </button>
          )}
        </div>
      )}

      {/* Type-appropriate details */}
      <div style={{ marginTop: 10, fontSize: 11.5, color: '#cbd5e1', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {description && <div style={{ color: '#cbd5e1' }}>{description}</div>}

        {meta.address && (
          <div>📍 <span style={{ color: '#e2e8f0' }}>{String(meta.address)}</span></div>
        )}

        {/* Buildings / floors / zones / slices — show the extrusion geometry. */}
        {(objectType === 'building' || objectType === 'floor' || objectType === 'zone' || objectType === 'slice') && heightM != null && (
          <div>
            📐 <span style={{ color: '#e2e8f0' }}>
              {tierCount >= 2
                ? `${heightM} m peak`
                : baseM != null && baseM > 0
                  ? `${baseM}–${heightM} m`
                  : `${heightM} m tall`}
            </span>
            {tierCount >= 2 && (
              <span style={{ color: '#94a3b8' }}> · {tierCount} tiers</span>
            )}
            {props.floorNumber != null && <span style={{ color: '#94a3b8' }}> · floor {props.floorNumber}</span>}
          </div>
        )}

        {/* Events — title, time, venue. Shown for any object carrying an
            occasion, not just `event_object`: a National Day belongs to
            the tower it lights up. */}
        {event?.title && (
          <div style={{ color: '#fde68a' }}>
            🎟  <strong style={{ color: '#fef3c7' }}>{event.title}</strong>
            {event.venue && <span style={{ color: '#94a3b8' }}> · {event.venue}</span>}
            {event.startsAt && <div style={{ color: '#94a3b8', fontSize: 11 }}>🕑 {new Date(event.startsAt).toLocaleString()}</div>}
          </div>
        )}

        {/* Real estate — price, status, deed contract. */}
        {objectType === 'real_estate' && realEstateData?.listingPriceUsd != null && (
          <div style={{ color: '#bbf7d0' }}>
            🏷  <strong style={{ color: '#dcfce7' }}>${Number(realEstateData.listingPriceUsd).toLocaleString()}</strong>
            {realEstateData.listingStatus && <span style={{ color: '#94a3b8' }}> · {realEstateData.listingStatus}</span>}
          </div>
        )}

        {/* Anything else useful we didn't render explicitly. */}
        {extraMeta.length > 0 && (
          <details style={{ marginTop: 2 }}>
            <summary style={{ cursor: 'pointer', color: '#94a3b8', fontSize: 11 }}>More details</summary>
            <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {extraMeta.map(([k, v]) => (
                <div key={k} style={{ display: 'flex', gap: 6 }}>
                  <span style={{ color: '#94a3b8', textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}:</span>
                  <span style={{ color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(v)}</span>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      {/* Linked execution rule */}
      {(interactions?.executionRule || props.contractAddress) && (
        <div style={{
          marginTop: 10, padding: '8px 10px',
          borderRadius: 8, background: 'rgba(99, 102, 241, 0.1)',
          border: '1px solid rgba(99, 102, 241, 0.35)',
          fontSize: 11.5,
        }}>
          <div style={{ color: '#a5b4fc', fontWeight: 700, marginBottom: 4 }}>
            ⚙ Linked execution
          </div>
          {interactions?.executionRule?.ruleId && (
            <div><strong style={{ color: '#e2e8f0' }}>Rule {interactions.executionRule.ruleId}</strong></div>
          )}
          {(props.contractAddress || interactions?.executionRule?.contractAddress) && (
            <div style={{ fontFamily: 'monospace', fontSize: 10.5, color: '#cbd5e1', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {props.contractAddress || interactions?.executionRule?.contractAddress}
              {(props.contractFunction || interactions?.executionRule?.contractFunction) && (
                <span style={{ color: '#a5b4fc' }}> · {props.contractFunction || interactions?.executionRule?.contractFunction}()</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Status / progress */}
      {(status === 'loading-interactions' || progress) && (
        <div style={{ marginTop: 10, fontSize: 11, color: '#fde68a' }}>
          {status === 'loading-interactions' ? 'Loading actions…' : progress}
        </div>
      )}

      {/* Decision badge — surfaces the GeoLink ruling. */}
      {lastDecision && (
        <div
          style={{
            marginTop: 10, padding: '6px 10px', borderRadius: 8,
            background: lastDecision.decision === 'allowed' ? 'rgba(34, 197, 94, 0.10)' : 'rgba(239, 68, 68, 0.10)',
            border:     lastDecision.decision === 'allowed'
              ? '1px solid rgba(34, 197, 94, 0.45)'
              : '1px solid rgba(239, 68, 68, 0.45)',
            color:      lastDecision.decision === 'allowed' ? '#86efac' : '#fca5a5',
            fontSize: 11.5,
          }}
        >
          GeoLink: {lastDecision.decision}
          {lastDecision.decisionReason ? ` · ${lastDecision.decisionReason}` : ''}
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{
          marginTop: 10, padding: '6px 10px', borderRadius: 8,
          background: 'rgba(239, 68, 68, 0.12)',
          border:     '1px solid rgba(239, 68, 68, 0.45)',
          color:      '#fca5a5', fontSize: 11.5,
        }}>
          ⚠ {error}
        </div>
      )}

      {/* Done with tx hash */}
      {status === 'done' && (
        <div style={{
          marginTop: 10, padding: '6px 10px', borderRadius: 8,
          background: 'rgba(34, 197, 94, 0.12)',
          border:     '1px solid rgba(34, 197, 94, 0.45)',
          color:      '#86efac', fontSize: 11.5,
        }}>
          ✓ Action completed
          {lastTxHash && (
            <div style={{ fontFamily: 'monospace', fontSize: 10.5, color: '#bbf7d0', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {lastTxHash}
            </div>
          )}
        </div>
      )}

      {/* Pre-sign disclosure — the consent boundary.
          Shows what GeoLink authorised, not what the button promised:
          the function and its arguments are resolved server-side from
          the linked rule, and until here the user has seen neither. */}
      {pending && (
        <div style={{
          marginTop: 10, padding: '10px 12px',
          borderRadius: 8, background: 'rgba(99, 102, 241, 0.12)',
          border: '1px solid rgba(165, 180, 252, 0.55)',
          fontSize: 11.5,
        }}>
          <div style={{ color: '#c7d2fe', fontWeight: 700, marginBottom: 5 }}>
            ⚠ Approve this contract call
          </div>
          <div style={{ color: '#cbd5e1', marginBottom: 7 }}>
            GeoLink authorised the call below. Nothing has been sent to your wallet yet.
          </div>

          <div style={{ color: '#94a3b8' }}>Contract</div>
          <div style={{ fontFamily: 'monospace', fontSize: 10.5, color: '#e2e8f0', overflowWrap: 'anywhere' }}>
            {pending.execution.contractAddress}
          </div>

          <div style={{ color: '#94a3b8', marginTop: 5 }}>Function</div>
          <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#a5b4fc' }}>
            {pending.execution.functionName}()
          </div>

          <div style={{ color: '#94a3b8', marginTop: 5 }}>
            Authorized parameters{pending.params.length === 0 ? ' — none' : ''}
          </div>
          {pending.params.length > 0 && (
            // Numbered because the order is the order: these are passed
            // positionally, so the list is the argument list.
            <ol style={{ margin: '2px 0 0', paddingLeft: 18, color: '#e2e8f0' }}>
              {pending.params.map((p, i) => (
                <li key={`${p.key}-${i}`} style={{ fontFamily: 'monospace', fontSize: 10.5, marginBottom: 3 }}>
                  <span style={{ color: '#a5b4fc' }}>{p.key}</span>
                  <span style={{ color: '#64748b' }}> · {p.encodedAs}</span>
                  <div style={{ color: '#f1f5f9', overflowWrap: 'anywhere' }}>
                    {formatDisclosedValue(p.value)}
                  </div>
                </li>
              ))}
            </ol>
          )}

          <div style={{ color: '#64748b', fontSize: 10.5, marginTop: 7 }}>
            Authorized at {pending.execution.createdAt} · shown exactly as GeoLink returned it
          </div>

          <div style={{ display: 'flex', gap: 6, marginTop: 9 }}>
            <button type="button" style={primaryBtn} onClick={() => void confirmExecution()}>
              Confirm &amp; sign
            </button>
            <button type="button" style={buttonBase} onClick={() => void cancelExecution()}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Action buttons — stood down while an authorisation is waiting. */}
      {interactions && interactions.actions.length > 0 && !pending && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {interactions.actions.map(action => {
            const busy = status !== 'idle' && status !== 'done' && status !== 'error';
            const needsWallet = !walletConnected && !!(
              preconditionsFor?.(action)?.wallet ||
              action.contractFunction ||
              hasContractLink ||
              /execut|sign|pay|claim|mint/i.test(`${action.label} ${action.id} ${action.name}`)
            );
            return (
              <button
                key={action.id}
                type="button"
                disabled={busy}
                onClick={() => void runAction(action)}
                title={action.description}
                style={{
                  ...primaryBtn,
                  opacity:    busy ? 0.55 : 1,
                  cursor:     busy ? 'default' : 'pointer',
                  textAlign:  'left',
                  whiteSpace: 'normal',
                  lineHeight: 1.35,
                }}
              >
                <div style={{ fontWeight: 700 }}>
                  {action.label}
                  {needsWallet && (
                    <span style={{ fontWeight: 500, fontSize: 10, color: '#fde68a', marginLeft: 6 }}>
                      wallet required
                    </span>
                  )}
                </div>
                {action.description && (
                  <div style={{ fontWeight: 500, fontSize: 11, color: '#e0e7ff', marginTop: 2 }}>
                    {action.description}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SpatialObjectInteractionPanel;
